import type { ReactNode } from 'react'

import { Card } from '@/components/ui/card'
import { MarkdownBody } from '@/components/ui/markdown-content'
import { TopSkillChip } from '@/components/profile/top-skill-chip'
import { stripRedundantLeadingHeading } from '@/lib/markdown-display'
import type { SkillSummary } from '@/hooks/use-profile'
import { cn } from '@/lib/utils'
import { takeTopDemonstratedSkills } from '@/utils/profile-skills-display'

function PublicProfileIntroBlock({
  title,
  className,
  children,
}: {
  title: string
  className?: string
  children: ReactNode
}) {
  return (
    <div className={cn('space-y-3', className)}>
      <h3 className="font-semibold leading-none">{title}</h3>
      {children}
    </div>
  )
}

export function PublicProfileAboutSkillsSection({
  bio,
  demonstratedSkillSummaries,
}: {
  bio?: string | null
  demonstratedSkillSummaries: SkillSummary[]
}) {
  const displayBody = stripRedundantLeadingHeading(bio?.trim() ?? '', ['About'])
  const topSkills = takeTopDemonstratedSkills(demonstratedSkillSummaries)

  if (!displayBody && topSkills.length === 0) {
    return null
  }

  return (
    <Card>
      {displayBody ? (
        <PublicProfileIntroBlock
          title="About"
          className={topSkills.length > 0 ? 'pb-6' : undefined}
        >
          <MarkdownBody>{displayBody}</MarkdownBody>
        </PublicProfileIntroBlock>
      ) : null}
      {topSkills.length > 0 ? (
        <PublicProfileIntroBlock title="Skills">
          <ul className="flex flex-wrap gap-2">
            {topSkills.map((summary) => (
              <li key={summary.skill_id}>
                <TopSkillChip summary={summary} displayOnly showTooltip={false} />
              </li>
            ))}
          </ul>
        </PublicProfileIntroBlock>
      ) : null}
    </Card>
  )
}
